import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import { AppView, CardView, SettingsItem, SettingsItemTitle, SettingsItemContent, SettingsItemDescription, } from 'design-system';
import Text from 'design-system/Text';
import { IconNote, IconTrash, IconUser } from 'design-system/icons';
import IconTransfer from 'design-system/icons/IconTransfer';
import IconBlock from 'design-system/icons/IconBlock';
import SectionHeader from 'design-system/SectionHeader';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import IconTicketSolid from 'design-system/icons/IconTicketSolid';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { getPreferences } from 'actions/user';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { formatBuyBlock } from '../utils/format';
import { getPatternDayTradingInfo } from '../actions';
import AlertSection from '../components/AlertSection';
import { selectPositionsLength } from '../hooks/selectors';
import InvestingSection from '../components/InvestingSection';
import { selectShowISATransfers, selectTotalDayTradeCount, selectTradingAccount } from '../reducers/selectors';
import { blockInvestActionOnWeb, blockInvestOnboardingOnWeb } from '../webGate';
import { isWeb } from '../../../constants';
const InvestSettings = withAnimatedNavigationHeader(({ scrollHandler, navigation }) => {
    const dispatch = useAppDispatch();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', undefined, paddingHorizontalStyle);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const isAlpacaAccount = tradingAccount?.isAlpaca;
    const showISATransfers = useAppSelector(selectShowISATransfers);
    const positionsLength = useAppSelector(selectPositionsLength);
    const totalDayTradeCount = useAppSelector(selectTotalDayTradeCount);
    const tradingBlockExpiryDate = tradingAccount?.account?.tradingBlockExpiryDate;
    const balance = isAlpacaAccount ? 0 : tradingAccount?.account.portfolioValue?.value || 0;
    const childDetails = tradingAccount?.account.accountType === 'JISA' ? tradingAccount?.account.child : undefined;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    useEffect(() => {
        dispatch(getPreferences());
        dispatch(getPatternDayTradingInfo());
    }, []);
    const handleAnnualCostsAndCharges = useCallback(() => {
        navigation.navigateDeprecated('AnnualCostsAndCharges');
    }, []);
    const handleCapitalGainsAndIncome = useCallback(() => {
        navigation.navigateDeprecated('ExportStatement', { type: 'CapitalGainsAndIncome' });
    }, []);
    const handleDayTradeCountPress = useCallback(() => {
        navigation.navigateDeprecated('DayTradeCountScreen');
    }, []);
    const handleBuyBlockPress = useCallback(() => {
        navigation.navigateDeprecated('BuyBlockScreen');
    }, []);
    const handleDeleteInvestmentAccount = useCallback(() => {
        if (positionsLength || balance) {
            navigation.navigateDeprecated('PositionsInfo');
            return;
        }
        if (blockInvestActionOnWeb('Close account'))
            return;
        navigation.navigateDeprecated('DeleteInvestAccountReason');
    }, [balance, positionsLength]);
    const handleValuationStatement = useCallback(() => {
        navigation.navigateDeprecated('ValuationStatement');
    }, []);
    const accountType = tradingAccount?.account?.accountType;
    const goToProductTransfer = useMemo(() => showISATransfers && accountType === 'ISA'
        ? () => {
            if (blockInvestOnboardingOnWeb())
                return;
            navigation.navigate('ProductTransferIntro');
        }
        : undefined, [accountType, showISATransfers]);
    const handleChildDetails = useCallback(() => {
        navigation.navigate('JISAChildDetails');
    }, []);
    return (<AppView type="secondary" withFrame={false}>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          {!isAlpacaAccount && (<SectionHeader horizontalMargins largeVerticalMargins>
              Accounts
            </SectionHeader>)}
          {!!childDetails && (<CardView noMarginTop>
              <SettingsItem title="Child’s details" onPress={handleChildDetails} icon={<IconUser gradientKey="lightPink"/>}/>
            </CardView>)}
          {isAlpacaAccount ? (<>
              <SectionHeader horizontalMargins largeVerticalMargins>
                Statements
              </SectionHeader>
              <CardView noMarginTop>
                <SettingsItem title="Annual costs & charges" onPress={handleAnnualCostsAndCharges} icon={<IconNote alt gradientKey="ultimate"/>}/>
                <SettingsItem title="Trading activity statement" onPress={handleCapitalGainsAndIncome} icon={<IconNote alt gradientKey="darkPink"/>}/>
              </CardView>
            </>) : (
        // On web the card adds 8 around the rows, so their hover sits inside the card
        <CardView noMarginTop {...(isWeb && { webPadding: true })}>
              <SettingsItem title="Valuation statement" onPress={handleValuationStatement} icon={<IconNote alt gradientKey="ultimate"/>}/>
              {goToProductTransfer !== undefined && (<SettingsItem title="Transfer an ISA" description="Bring your ISAs together for easier management" onPress={goToProductTransfer} icon={<IconTransfer alt gradientKey="skyBlue"/>}/>)}
            </CardView>)}
          {!!isAlpacaAccount && (<>
              <InvestingSection />

              <AlertSection />

              <SectionHeader horizontalMargins largeVerticalMargins>
                Protection
              </SectionHeader>
              <TouchableBounceCard onDark noMarginTop onPress={handleBuyBlockPress}>
                <SettingsItem title="Buy block" icon={<IconBlock gradientKey="apricot"/>} description={tradingBlockExpiryDate ? (<Text TextThin-14 Secondary>
                        Expires on{' '}
                        <Text Text-14 Secondary>
                          {formatBuyBlock(tradingBlockExpiryDate)}
                        </Text>
                      </Text>) : ('Temporarily pause trading in Emma')}/>
              </TouchableBounceCard>
              {totalDayTradeCount !== undefined && (<TouchableBounceCard onDark noMarginTop onPress={handleDayTradeCountPress}>
                  <SettingsItem title="Day trade count" icon={<IconTicketSolid colorKey="brand"/>} description="Your trades within the same day on the same stock">
                    <SettingsItemContent>
                      <Text Numbers-12 Primary>
                        {totalDayTradeCount}/3
                      </Text>
                      <SettingsItemTitle>Day trade count</SettingsItemTitle>
                      <SettingsItemDescription>
                        Your trades within the same day on the same stock
                      </SettingsItemDescription>
                    </SettingsItemContent>
                  </SettingsItem>
                </TouchableBounceCard>)}
            </>)}

          {isWeb ? (
        // A whole-card press target would highlight to the card edge, so web uses a padded card and row
        <CardView noMarginTop webPadding>
              <SettingsItem title="Delete investment account" onPress={handleDeleteInvestmentAccount} icon={<IconTrash gradientKey="peach"/>}/>
            </CardView>) : (<TouchableBounceCard onDark noMarginTop onPress={handleDeleteInvestmentAccount}>
              <SettingsItem title="Delete investment account" icon={<IconTrash gradientKey="peach"/>}/>
            </TouchableBounceCard>)}
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: 'Settings',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default InvestSettings;
